const post = {
  slug: "checkout-dm-free-plan-limits-and-when-to-upgrade",
  title: "Checkout DM Free Plan: Limits and When to Upgrade",
  seoTitle: "Checkout DM Free Plan: Limits and Upgrades",
  description:
    "See what the Checkout DM Free plan includes, its two limits of 5 products and 15 conversations a month, what it leaves out and the signs it is time to upgrade.",
  excerpt:
    "The Free plan is a real way to test DM selling with real customers. Here is what it gives you, where it stops and how to tell when you have outgrown it.",
  category: "Product",
  tags: ["Checkout DM free plan", "Checkout DM pricing", "Free Instagram DM checkout"],
  date: "2026-10-30T12:00:00Z",
  cover: {
    src: "/blog-images/checkout-dm-free-plan-limits-and-when-to-upgrade-cover.jpg",
    alt: "A phone showing the Checkout DM Free plan with 5 products and 15 conversations a month",
  },
  blocks: [
    {
      type: "p",
      text:
        "Before paying for any tool, most sellers want to know whether they can try it properly first. Checkout DM has a Free plan at \u20B90 that lets you take real orders from real customers, with two clear limits. This guide explains what the plan includes, where it stops and how to tell when it is time to move up.",
    },

    { type: "h2", text: "What Free includes" },
    {
      type: "p",
      text:
        "Free is not a demo. It includes the core DM checkout, so a customer can browse your catalog, build a cart, give a delivery address and pay through a checkout link sent in the same chat. Specifically, you get:",
    },
    {
      type: "ul",
      items: [
        "**Full DM checkout automation** with the catalog, cart and payment link handled inside Instagram DMs.",
        "**Payments your way.** UPI, bank transfer and QR code with manual confirmation, or Razorpay, Stripe and PayPal for automatic confirmation.",
        "**Return and exchange handling** in DMs, with your return policy, exchange policy and FAQs shown automatically.",
        "**A shipping fee at checkout** and **shipped emails** to your customers.",
        "**Digital delivery**, so a digital product can be sent by link after payment.",
        "**Email support.**",
      ],
    },

    { type: "h2", text: "The two limits" },
    {
      type: "table",
      headers: ["", "Free plan"],
      rows: [
        ["Price", "\u20B90 ($0)"],
        ["Products in your catalog", "Up to 5"],
        ["Customer conversations", "15 unique customer conversations per month"],
        ["Checkout DM branding", "Shown in DMs, checkout and emails"],
      ],
    },
    {
      type: "p",
      text:
        "**Products.** You can list up to 5. That is enough to test your best sellers, but not a full catalog.",
    },
    {
      type: "p",
      text:
        "**Conversations.** Free handles 15 unique customer conversations per month. When you reach the limit, new customer conversations pause until your monthly limit resets, or until you upgrade to a paid plan with unlimited conversations.",
    },
    {
      type: "callout",
      tone: "lilac",
      title: "Plan around the limit",
      text:
        "If you are about to run a launch, a sale or a Reel that could bring many new customers in a day, remember that 15 conversations is a small number. Upgrade before the campaign rather than in the middle of it, so no new customer is left waiting.",
    },

    { type: "h2", text: "What Free leaves out" },
    {
      type: "p",
      text: "These features start on a paid plan, so you will not see them on Free:",
    },
    {
      type: "ul",
      items: [
        "**Comment-to-DM automation** starts on Creator.",
        "**Removing Checkout DM branding.** Creator removes it from DMs, and Starter removes it everywhere.",
        "**Delivery status tracking and automatic stock updates** start on Starter.",
        "**Abandoned-cart information** starts on Starter, and automatic multi-step recovery starts on Growth.",
        "**Coupons and offers** start on Growth.",
        "**Product-matched Story replies** start on Growth. Free uses generic Story replies.",
        "**Team members** start on Pro. Free has one user.",
      ],
    },

    { type: "h2", text: "How to test properly on Free" },
    {
      type: "p",
      text:
        "A free plan is only useful if you learn something from it. Here is a simple way to spend your first two weeks:",
    },
    {
      type: "ol",
      items: [
        "**Connect your Instagram Business account.** If you hit an error, our guide to [connecting your Instagram account to Checkout DM](/blog/connect-instagram-business-account-checkout-dm) covers the common fixes.",
        "**List your best five products** with clear photos, prices and sizes. Our [catalog tips](/blog/instagram-catalog-tips-photos-sizes) help here.",
        "**Set up how you get paid.** UPI or bank transfer works with manual confirmation. For automatic confirmation, see [how to connect Razorpay](/blog/connect-razorpay-to-checkout-dm).",
        "**Place a test order from a second account** and walk through the whole flow as a customer.",
        "**Tell real customers.** Post a Story or Reel that points people to your DMs and see how many message you.",
        "**Note what you wish you had.** After two weeks, the missing feature you keep wanting tells you which plan to pick.",
      ],
    },

    { type: "h2", text: "When to upgrade" },
    {
      type: "p",
      text:
        "Move up when a limit or a missing feature costs you more than the plan would. These are the usual signals:",
    },
    {
      type: "ul",
      items: [
        "**You have more than 5 products to sell.** For physical products, Starter lifts the limit to 500. For digital products only, Creator allows 150.",
        "**You are close to 15 conversations a month.** Every paid plan has unlimited conversations.",
        "**You want Comment-to-DM on your Reels.** That starts on Creator. See [how Comment-to-DM works](/blog/comment-to-dm-instagram-orders).",
        "**You do not want Checkout DM branding in front of your customers.**",
        "**You are chasing late deliveries and abandoned carts by hand.** Starter adds delivery tracking and cart information, and Growth automates the recovery.",
      ],
    },
    {
      type: "p",
      text:
        "For a plan-by-plan match, read [which Checkout DM plan you should choose](/blog/which-checkout-dm-plan-should-you-choose). For current prices and the full feature lists, see the [Pricing page](/pricing).",
    },

    { type: "h2", text: "What Free does not charge" },
    {
      type: "p",
      text:
        "There is no commission or transaction fee on Free or on any other plan, and your customers never pay Checkout DM anything. Payments go directly to your own payment account. The only costs outside the plan are your own: the fee your payment gateway charges if you use one, and your courier. Our guide to [what Checkout DM really costs](/blog/what-checkout-dm-really-costs-plan-gateway-fees) walks through each of them.",
    },

    { type: "h2", text: "Changing plans later" },
    {
      type: "p",
      text:
        "You can upgrade or downgrade at any time without losing your products, orders or customer history, and there is no long-term contract. That means starting on Free carries no risk of being stuck: you can leave it the day a limit starts to hurt.",
    },
  ],
  related: [
    { to: "/pricing", title: "Checkout DM pricing", description: "Current plans, prices and the full feature comparison." },
    { to: "/faq", title: "Frequently Asked Questions", description: "Answers on plans, payments, shipping and returns." },
    { to: "/help", title: "Help Guide", description: "How to set up and run your Checkout DM account." },
  ],
  faqs: [
    {
      q: "Is the Checkout DM Free plan really free?",
      a: "Yes. Free is priced at \u20B90 ($0) and takes no commission or transaction fee. It has limits of 5 products and 15 unique customer conversations per month.",
    },
    {
      q: "What happens when I reach 15 conversations on Free?",
      a: "New customer conversations pause until your monthly limit resets, or until you upgrade to a paid plan with unlimited conversations.",
    },
    {
      q: "Can I sell digital products on the Free plan?",
      a: "Yes, within the 5-product limit. Every plan can deliver a digital product by link after payment.",
    },
    {
      q: "Will my customers see Checkout DM branding on the Free plan?",
      a: "Yes. On Free, Checkout DM branding is shown to customers in DMs, checkout and emails.",
    },
    {
      q: "Can I upgrade without losing my data?",
      a: "Yes. You can upgrade or downgrade at any time without losing your products, orders or customer history.",
    },
  ],
};

export default post;
